import { Beacon, CreateBeaconRequest } from '@/api';
import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { UIToast } from '@/ui';
import { useDevicesTranslation } from '../hooks';
import { addOrUpdateAllBeaconsCache } from '../queries/devices-cache';

type UseCreateBeaconActionData = {
    createBeacon: (data: CreateBeaconRequest) => Promise<Beacon>;
    loading: boolean;
    error: string;
};

/**
 * Adding a beacon to a company.
 *
 * The company is part of the request rather than read from context: the form
 * already knows which company's beacon list it is adding to, and beacons are
 * reachable from screens outside the selected company.
 *
 * No `isAllowed`/`reason`: the gate is the beacons page's own
 * `canEditBeacons`, which hides the button that opens this form.
 */
export function useCreateBeaconAction(): UseCreateBeaconActionData {
    const { t } = useDevicesTranslation();
    // The client is swapped out on token refresh, so never capture it: read
    // `openApiRef.current` at call time or you keep using a dead token.
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error
    // `POST /v3/devices/beacons` declares gets a branch, bar
    // `COMPANY_NOT_FOUND`, which this namespace has nothing of its own to say
    // about and so falls through to the generic message.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            if (isApiError(error, 'BEACON_ALREADY_EXISTS')) {
                return t('shared.errors.beacon_already_exists');
            }
            if (isApiError(error, 'FORBIDDEN_RESOURCE')) {
                return t('shared.errors.unauthorised');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                // The server names the offending field; it is more use than
                // anything generic we could write.
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: (data: CreateBeaconRequest) =>
            openApiRef.current.devices
                .createBeacon(data)
                .then((response) => response.data.data.beacon),
        onSuccess: (beacon) => {
            addOrUpdateAllBeaconsCache(queryClient, beacon);

            UIToast.success(t('actions.create_beacon.success'));
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        createBeacon: (data) => mutation.mutateAsync(data),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
